import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Space, Typography, message } from "antd";
import { EditOutlined, SaveOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { updateRate } from "../../api/rateApi";
import { useTranslation } from "../../i18n/LanguageProvider";
import RateFormFields from "./RateFormFields";
import { buildRatePayload } from "./rateUi";

const { Text } = Typography;

/**
 * Edit of one Ханш row. Uses the same field set as manual entry, pre-filled from the record.
 */
function RateEditModal({ open, record, institutionOptions = [], onClose, onSaved }) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open || !record) return;

        setError("");
        form.setFieldsValue({
            name: record.name || "",
            currency: record.currency || undefined,
            rate: record.rate === null || record.rate === undefined
                ? undefined
                : Number(record.rate),
            institution: record.institution || "",
            amount: record.amount === null || record.amount === undefined
                ? undefined
                : Number(record.amount),
            executedDate: record.executedDate ? dayjs(record.executedDate) : undefined,
        });
    }, [open, record, form]);

    const handleSubmit = async () => {
        setError("");

        try {
            const values = await form.validateFields();

            setSaving(true);

            const saved = await updateRate(record.id, buildRatePayload(values));

            message.success(t("rate.edit.saved"));
            onSaved?.(saved);
            onClose?.();
        } catch (err) {
            if (err?.errorFields) return;

            const text = err?.message || t("rate.edit.saveFailed");
            setError(text);
            message.error(text);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={
                <Space>
                    <EditOutlined />
                    <span>{t("rate.edit.modalTitle")}</span>
                </Space>
            }
            open={open}
            onCancel={onClose}
            width={1000}
            destroyOnClose
            footer={
                <Space>
                    <Button onClick={onClose}>{t("action.close")}</Button>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={saving}
                        onClick={handleSubmit}
                    >
                        {t("action.save")}
                    </Button>
                </Space>
            }
        >
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Text type="secondary">{t("rate.edit.hint")}</Text>

                {error && (
                    <Alert type="error" showIcon message={t("edit.saveError")} description={error} />
                )}

                <Form form={form} layout="vertical" requiredMark autoComplete="off">
                    <RateFormFields t={t} institutionOptions={institutionOptions} />
                </Form>
            </Space>
        </Modal>
    );
}

export default RateEditModal;
